import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Input } from '../../components/atoms/Input'
import { Spinner } from '../../components/atoms/Spinner'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import { ROUTES } from '../../../constants/routes.constants'
import { SCREENCODES } from '../../../constants/screen.constants'
import { useAuth } from '../../../context/AuthContext'
import {
  findTemplateByCode,
  useNotificationTemplates,
  type NotificationTemplateDto
} from '../../../viewmodels/useNotificationTemplates'

/**
 * /dashboard/preferences/email-templates/:code — edit one template.
 *
 * Two-pane layout (Kiran 2026-05-14 picked side-by-side preview):
 *   left  : subject (text input) + html_body (textarea) + text_body (textarea)
 *   right : <iframe srcdoc={previewHtml}> showing the rendered email
 *           with {{code}} replaced by a placeholder value (654321).
 *
 * On mobile the preview pane stacks below the form so the editor
 * stays usable on phone (SOP 02 §"Mobile-First").
 *
 * No fallback (SOP 15 Ch.0.5) — if the template list fails to load
 * we surface the error; we do NOT show a stale or hardcoded
 * template for editing.
 */

const PREVIEW_CODE = '654321'

function substitute(template: string, code: string): string {
  return template.split('{{code}}').join(code)
}

export function NotificationTemplateEditPage(): JSX.Element {
  const navigate = useNavigate()
  const { can } = useAuth()
  const canEdit = can(SCREENCODES.PREFERENCESEMAILTEMPLATES, 'edit')
  const { code } = useParams<{ code: string }>()
  const { items, isLoading, error, update } = useNotificationTemplates()

  const initial = useMemo<NotificationTemplateDto | null>(
    () => (code !== undefined ? findTemplateByCode(items, code) : null),
    [items, code]
  )

  const [subject, setSubject] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [textBody, setTextBody] = useState('')
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (initial !== null) {
      setSubject(initial.subject)
      setHtmlBody(initial.html_body)
      setTextBody(initial.text_body)
      setDirty(false)
    }
  }, [initial])

  const previewHtml = useMemo(() => substitute(htmlBody, PREVIEW_CODE), [htmlBody])

  const canSave = dirty && subject.trim().length > 0 && htmlBody.length > 0 && textBody.length > 0 && !saving

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault()
    if (code === undefined) return
    setSubmitError(null)
    setFieldError(null)
    setSaving(true)
    const res = await update(code, {
      subject: subject.trim(),
      html_body: htmlBody,
      text_body: textBody
    })
    setSaving(false)
    if (res.ok) {
      setSavedAt(new Date(res.data.updated_at).toLocaleString())
      setDirty(false)
      return
    }
    if (res.field !== null) {
      setFieldError({ field: res.field, message: res.message })
    } else {
      setSubmitError(res.message)
    }
  }

  if (code === undefined) {
    return <Alert tone="error">Missing template code in URL.</Alert>
  }
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }
  if (error !== null) {
    return <Alert tone="error">{error}</Alert>
  }
  if (initial === null) {
    return (
      <Alert tone="error">
        Template not found: <span className="font-mono">{code}</span>.{' '}
        <Link to={ROUTES.PREFERENCESEMAILTEMPLATES} className="underline">
          Back to list
        </Link>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-h2 text-charcoal">{initial.name}</h1>
        <Link
          to={ROUTES.PREFERENCESEMAILTEMPLATES}
          className="font-body text-body-sm text-text-secondary hover:text-charcoal"
        >
          ← Back
        </Link>
      </header>

      {submitError !== null ? <Alert tone="error">{submitError}</Alert> : null}
      {savedAt !== null && !dirty ? (
        <Alert tone="success">Saved at {savedAt}.</Alert>
      ) : null}

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Editor pane */}
        <div className="space-y-4">
          <Field
            label="Subject"
            error={fieldError?.field === 'subject' ? fieldError.message : null}
          >
            <Input
              value={subject}
              hasError={fieldError?.field === 'subject'}
              onChange={(e) => {
                setSubject(e.target.value)
                setDirty(true)
              }}
              maxLength={256}
            />
          </Field>

          <Field
            label="HTML body"
            error={fieldError?.field === 'html_body' ? fieldError.message : null}
            helper="Inline-styled HTML. Use {{code}} where the 6-digit code should appear."
          >
            <Textarea
              value={htmlBody}
              rows={14}
              hasError={fieldError?.field === 'html_body'}
              onChange={(e) => {
                setHtmlBody(e.target.value)
                setDirty(true)
              }}
              className="font-mono text-body-xs"
            />
          </Field>

          <Field
            label="Plain-text body"
            error={fieldError?.field === 'text_body' ? fieldError.message : null}
            helper="Shown to email clients that strip HTML. Use {{code}} too."
          >
            <Textarea
              value={textBody}
              rows={6}
              hasError={fieldError?.field === 'text_body'}
              onChange={(e) => {
                setTextBody(e.target.value)
                setDirty(true)
              }}
              className="font-mono text-body-xs"
            />
          </Field>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            {canEdit ? (
              <Button type="submit" variant="primary" size="lg" disabled={!canSave} className="flex-1">
                {saving ? 'Saving…' : 'Save'}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              size="lg"
              onClick={() => navigate(ROUTES.PREFERENCESEMAILTEMPLATES)}
              disabled={saving}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>

        {/* Preview pane */}
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="block text-body-sm font-body text-text-primary">Preview</span>
            <span className="text-body-xs text-text-secondary font-mono">
              {substitute(subject, PREVIEW_CODE)}
            </span>
          </div>
          <iframe
            title="Email preview"
            srcDoc={previewHtml}
            className="w-full h-[640px] rounded-md border border-border bg-bg-primary"
            sandbox=""
          />
        </div>
      </form>
    </div>
  )
}

